/**
 * Drop targets for the other stages, on the narrow layout only.
 *
 * The wide board shows all nine columns, so a card is dragged from one to
 * another. The narrow layout shows **one** column at a time (plan §7.3) — there
 * is no second column on screen to drop onto, which would leave a touch user
 * with no way to move a card at all now that the per-card select is gone.
 *
 * These are the other eight stages as a wrapped row of droppable chips. Dragging
 * is still the only mechanism, which is what keeps one mental model across both
 * layouts; only the target changes shape when there is no room for a column.
 *
 * Each chip highlights as the card passes over it, so the drop is aimed rather
 * than guessed at.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.9, §7.3
 */
import { useDroppable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';

import { STAGE_COLOR, type Stage } from '../../../components/domain/stages';
import { cn } from '../../../lib/cn';

/** One stage, as a drop target. */
function StageTarget({ stage }: { stage: Stage }): React.JSX.Element {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: stage });

  return (
    <li
      ref={setNodeRef}
      className={cn(
        'border-gray-200 text-body flex min-h-11 items-center gap-2 rounded-full',
        'border px-3.5 text-xs transition-colors',
        /* `isOver` is dnd-kit drag state, set from element geometry jsdom does
           not compute, so it is unreachable here and verified in a browser. */
        /* v8 ignore next */
        isOver ? 'border-blue bg-blue-light text-navy-text font-semibold' : 'bg-white',
      )}
    >
      <span
        aria-hidden="true"
        className="size-[7px] flex-none rounded-full"
        style={{ backgroundColor: STAGE_COLOR[stage] }}
      />
      {t(`stage.${stage}`)}
    </li>
  );
}

export interface StageDropTargetsProps {
  /** The stage currently on screen, which is not offered as a target. */
  current: Stage;
  stages: readonly Stage[];
}

export function StageDropTargets({
  current,
  stages,
}: StageDropTargetsProps): React.JSX.Element {
  const { t } = useTranslation();
  const targets = stages.filter((stage) => stage !== current);

  return (
    <section aria-label={t('board.dropTargets')} className="mt-s3">
      <h2 className="text-body font-display mb-s2 text-[10px] font-extrabold tracking-[0.12em] uppercase">
        {t('board.dropTargets')}
      </h2>
      <ul className="gap-s2 flex flex-wrap">
        {targets.map((stage) => (
          <StageTarget key={stage} stage={stage} />
        ))}
      </ul>
    </section>
  );
}
